import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Flex, Text, HStack } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMusic, FiAlertCircle, FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import {
  parseSpotifyEmbed,
  parseYouTubeEmbed,
  parseYouTubePlayerParams,
  detectUrlType,
} from '../utils/urlParser';
import YouTubeAudioDock from './YouTubeAudioDock';
import {
  getMusicRecents,
  addMusicRecent,
  removeMusicRecent,
  patchMusicRecentByUrl,
} from '../utils/storage';
import { formatMusicRecentDisplayLine } from '../utils/musicRecentLabels';
import { fetchSpotifyOEmbedTitle } from '../utils/spotifyOembed';
import { fetchYouTubeOEmbedTitle } from '../utils/youtubeOembed';

const MotionBox = motion(Box);

const SourceTab = ({ label, active, accent, theme, onClick }) => (
  <Box
    as="button"
    type="button"
    flex={1}
    h="40px"
    px={1}
    py={0}
    borderRadius="md"
    cursor="pointer"
    display="flex"
    alignItems="center"
    justifyContent="center"
    onClick={onClick}
    style={{
      background: active ? `${accent}24` : 'transparent',
      border: active ? `1px solid ${accent}50` : '1px solid transparent',
      boxSizing: 'border-box',
      transition: 'background 0.2s ease, border-color 0.2s ease',
    }}
    _hover={!active ? { background: 'rgba(255,255,255,0.05)' } : {}}
  >
    <Text
      as="span"
      fontSize="11px"
      fontWeight={active ? '600' : '500'}
      lineHeight="1"
      m={0}
      p={0}
      textAlign="center"
      style={{ color: active ? accent : theme.textMuted }}
    >
      {label}
    </Text>
  </Box>
);

const PresTab = ({ label, active, onClick, theme }) => (
  <Box
    as="button"
    type="button"
    flex={1}
    h="36px"
    px={2}
    py={0}
    borderRadius="md"
    cursor="pointer"
    display="flex"
    alignItems="center"
    justifyContent="center"
    onClick={onClick}
    style={{
      background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
      border: active ? '1px solid rgba(255,255,255,0.14)' : '1px solid transparent',
      boxSizing: 'border-box',
    }}
  >
    <Text
      as="span"
      fontSize="10px"
      fontWeight={active ? '600' : '500'}
      lineHeight="1"
      m={0}
      p={0}
      textAlign="center"
      style={{ color: active ? theme.textPrimary : theme.textDim }}
    >
      {label}
    </Text>
  </Box>
);

/** URL field + compact Recent trigger; list opens in a quiet anchored panel (not focus-driven). */
const MusicUrlRow = ({
  value,
  onChange,
  placeholder,
  theme,
  error,
  recents,
  menuOpen,
  onMenuOpenChange,
  onPickRecent,
  onRemoveRecent,
  constrainWidth = true,
}) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (!wrapRef.current?.contains(e.target)) onMenuOpenChange(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen, onMenuOpenChange]);

  const hasRecents = recents.length > 0;

  return (
    <Box ref={wrapRef} w="100%" maxW={constrainWidth ? '400px' : '100%'} mx="auto">
      <Flex gap={2} align="stretch" position="relative" justify="center" w="100%">
        <Box flex={1} minW={0} display="flex" alignItems="stretch">
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="lg"
            px={3}
            h="44px"
            style={{
              background: theme.inputBg,
              border: `1px solid ${error ? '#f87171' : theme.cardBorder}`,
              boxSizing: 'border-box',
              transition: 'border-color 0.2s ease',
            }}
          >
            <input
              className="fl-input fl-music-url-input"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              style={{
                color: value ? theme.textPrimary : theme.textDim,
                fontSize: '12px',
                textAlign: 'center',
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </Box>
        </Box>
        {hasRecents && (
          <Box
            as="button"
            type="button"
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="44px"
            minW="96px"
            px={3}
            py={0}
            borderRadius="lg"
            cursor="pointer"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            onClick={() => onMenuOpenChange(!menuOpen)}
            style={{
              background: menuOpen ? 'rgba(255,255,255,0.07)' : theme.inputBg,
              border: `1px solid ${theme.cardBorder}`,
              boxSizing: 'border-box',
              color: theme.textMuted,
              transition: 'background 0.15s ease',
            }}
          >
            <Text
              as="span"
              fontSize="11px"
              fontWeight="500"
              letterSpacing="0.06em"
              textAlign="center"
              lineHeight="1"
              m={0}
              p={0}
            >
              Recent
            </Text>
          </Box>
        )}

        {menuOpen && hasRecents && (
          <Box
            role="listbox"
            aria-label="Recent links"
            position="absolute"
            right={0}
            top="100%"
            mt={1.5}
            w="min(100vw - 48px, 300px)"
            maxW="300px"
            zIndex={400}
            borderRadius="md"
            overflow="hidden"
            className="fl-scroll"
            style={{
              background: 'rgba(10,8,18,0.98)',
              border: `1px solid ${theme.cardBorder}`,
              boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(14px)',
              maxHeight: '240px',
            }}
          >
            <Box px={2.5} py={1.5} style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
              <Text
                fontSize="9px"
                fontWeight="600"
                letterSpacing="0.14em"
                textTransform="uppercase"
                style={{ color: theme.textDim }}
              >
                Recent
              </Text>
            </Box>
            {recents.map((entry, i) => {
              const line = formatMusicRecentDisplayLine(entry);
              return (
                <Flex
                  key={`${entry.source}:${entry.url}`}
                  align="center"
                  gap={1}
                  w="100%"
                  pl={2.5}
                  pr={1}
                  py={1.5}
                  role="option"
                  cursor="pointer"
                  style={{
                    background: 'transparent',
                    borderBottom: i < recents.length - 1 ? `1px solid ${theme.cardBorder}` : 'none',
                  }}
                  _hover={{ background: 'rgba(255,255,255,0.035)' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPickRecent(entry);
                  }}
                >
                  <Text
                    flex={1}
                    minW={0}
                    fontSize="11px"
                    fontWeight="500"
                    lineHeight="1.35"
                    textAlign="left"
                    noOfLines={1}
                    style={{ color: theme.textMuted }}
                    title={line}
                  >
                    {line}
                  </Text>
                  <Box
                    as="button"
                    type="button"
                    aria-label="Remove from recent"
                    flexShrink={0}
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="md"
                    border="none"
                    cursor="pointer"
                    style={{
                      background: 'transparent',
                      color: theme.textDim,
                    }}
                    _hover={{ background: 'rgba(255,255,255,0.08)', color: theme.textMuted }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveRecent(entry.url);
                    }}
                  >
                    <FiX size={14} strokeWidth={2.25} />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        )}
      </Flex>
      {error && (
        <Flex align="center" justify="center" gap={1.5} mt={1.5} flexWrap="wrap" textAlign="center">
          <FiAlertCircle size={11} color="#f87171" style={{ flexShrink: 0 }} />
          <Text fontSize="10px" color="#f87171" maxW="100%">{error}</Text>
        </Flex>
      )}
    </Box>
  );
};

const EmbedChrome = ({ children, label, sub, badge, accent, theme }) => {
  const showMeta = Boolean(label || sub || badge);
  return (
  <Box mt={3}>
    {showMeta && (
      <Flex align="center" justify="space-between" mb={2} px={0.5} flexWrap="wrap" gap={2}>
        {label ? (
          <Text fontSize="10px" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase" style={{ color: theme.textMuted }}>
            {label}
          </Text>
        ) : <Box />}
        <HStack spacing={2}>
          {badge && (
            <Text fontSize="9px" fontWeight="700" letterSpacing="0.08em" px={2} py={0.5} borderRadius="full" style={{ color: accent, border: `1px solid ${accent}44`, background: `${accent}12` }}>
              {badge}
            </Text>
          )}
          {sub ? (
            <Text fontSize="10px" style={{ color: theme.textDim }}>{sub}</Text>
          ) : null}
        </HStack>
      </Flex>
    )}
    <Box
      borderRadius="xl"
      p="3px"
      style={{
        background: `linear-gradient(145deg, ${accent}22, rgba(255,255,255,0.04))`,
        boxShadow: `0 0 0 1px ${accent}28, 0 12px 40px rgba(0,0,0,0.35)`,
      }}
    >
      <Box borderRadius="lg" overflow="hidden" style={{ background: 'rgba(0,0,0,0.25)' }}>
        {children}
      </Box>
    </Box>
  </Box>
  );
};

/** Standard visible YouTube embed (iframe). */
const YouTubeStandardPlayer = ({ src }) => (
  <Box className="fl-yt-shell" maxH="220px" overflow="hidden">
    <iframe
      title="YouTube"
      src={src}
      width="100%"
      height="240"
      style={{ display: 'block', marginTop: '-24px' }}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      loading="lazy"
    />
  </Box>
);

const MusicPanel = ({ musicState, onMusicChange, theme, variant = 'full' }) => {
  const {
    source, spotifyUrl, youtubeUrl,
    youtubePresentation = 'audio_first',
    youtubeVolume = 80,
    youtubeMuted = false,
  } = musicState;

  const setPresentation = (youtubePresentation) =>
    onMusicChange({ ...musicState, youtubePresentation });

  const [spotifyInput, setSpotifyInput] = useState(spotifyUrl);
  const [youtubeInput, setYoutubeInput] = useState(youtubeUrl);
  const [spotifyError, setSpotifyError] = useState('');
  const [youtubeError, setYoutubeError] = useState('');
  const [dockOpen, setDockOpen] = useState(false);
  const [recents, setRecents] = useState(() => getMusicRecents());
  const [recentMenuOpen, setRecentMenuOpen] = useState(false);

  const spotifyRecents = recents.filter((r) => r.source === 'spotify');
  const youtubeRecents = recents.filter((r) => r.source === 'youtube');

  useEffect(() => { setSpotifyInput(spotifyUrl); }, [spotifyUrl]);
  useEffect(() => { setYoutubeInput(youtubeUrl); }, [youtubeUrl]);
  useEffect(() => { setRecentMenuOpen(false); }, [source]);

  const refreshSpotifyTitleForUrl = useCallback((url) => {
    const u = url?.trim();
    if (!u) return;
    fetchSpotifyOEmbedTitle(u).then((title) => {
      if (!title) return;
      patchMusicRecentByUrl(u, { spotifyTitle: title });
      setRecents(getMusicRecents());
    });
  }, []);

  const refreshYouTubeTitleForUrl = useCallback((url) => {
    const u = url?.trim();
    if (!u) return;
    fetchYouTubeOEmbedTitle(u).then((title) => {
      if (!title) return;
      patchMusicRecentByUrl(u, { youtubeTitle: title });
      setRecents(getMusicRecents());
    });
  }, []);

  useEffect(() => {
    if (spotifyUrl?.trim()) {
      addMusicRecent({ url: spotifyUrl.trim(), source: 'spotify' });
      refreshSpotifyTitleForUrl(spotifyUrl.trim());
    }
    if (youtubeUrl?.trim()) {
      addMusicRecent({ url: youtubeUrl.trim(), source: 'youtube', youtubePresentation });
      refreshYouTubeTitleForUrl(youtubeUrl.trim());
    }
    setRecents(getMusicRecents());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed persisted URLs into unified recents once on mount
  }, []);

  useEffect(() => {
    if (!recentMenuOpen || (source !== 'spotify' && source !== 'youtube')) return;
    const titleKey = source === 'spotify' ? 'spotifyTitle' : 'youtubeTitle';
    const fetchTitle = source === 'spotify' ? fetchSpotifyOEmbedTitle : fetchYouTubeOEmbedTitle;
    const list = getMusicRecents().filter((r) => r.source === source && !r[titleKey]?.trim());
    if (list.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const e of list) {
        if (cancelled) break;
        const title = await fetchTitle(e.url);
        if (cancelled || !title) continue;
        patchMusicRecentByUrl(e.url, { [titleKey]: title });
        if (!cancelled) setRecents(getMusicRecents());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recentMenuOpen, source]);

  const accent =
    source === 'spotify' ? '#1DB954'
    : source === 'youtube' ? '#e53935'
    : theme.focusAccent;

  const setSource = (s) => onMusicChange({ ...musicState, source: s });

  const applyRecent = useCallback((entry) => {
    if (entry.source === 'spotify') {
      onMusicChange({ ...musicState, source: 'spotify', spotifyUrl: entry.url });
      setSpotifyInput(entry.url);
      setSpotifyError('');
    } else {
      onMusicChange({
        ...musicState,
        source: 'youtube',
        youtubeUrl: entry.url,
        youtubePresentation: entry.youtubePresentation || 'audio_first',
      });
      setYoutubeInput(entry.url);
      setYoutubeError('');
    }
    setRecentMenuOpen(false);
  }, [musicState, onMusicChange]);

  const handleRemoveRecent = useCallback((url) => {
    removeMusicRecent(url);
    const next = getMusicRecents();
    setRecents(next);
    if (next.length === 0) setRecentMenuOpen(false);
  }, []);

  const handleSpotifyChange = (val) => {
    setSpotifyInput(val);
    setSpotifyError('');
    if (!val.trim()) {
      onMusicChange({ ...musicState, spotifyUrl: '' });
      return;
    }
    if (detectUrlType(val) !== 'spotify') {
      setSpotifyError('Spotify playlist, album, or track link');
      return;
    }
    const embed = parseSpotifyEmbed(val);
    if (!embed) {
      setSpotifyError('Could not read that link');
      return;
    }
    onMusicChange({ ...musicState, spotifyUrl: val });
    addMusicRecent({ url: val.trim(), source: 'spotify' });
    setRecents(getMusicRecents());
    refreshSpotifyTitleForUrl(val.trim());
  };

  const handleYoutubeChange = (val) => {
    setYoutubeInput(val);
    setYoutubeError('');
    if (!val.trim()) {
      onMusicChange({ ...musicState, youtubeUrl: '' });
      return;
    }
    if (detectUrlType(val) !== 'youtube') {
      setYoutubeError('YouTube video or playlist link');
      return;
    }
    const embed = parseYouTubeEmbed(val);
    if (!embed) {
      setYoutubeError('Unsupported or private — try another URL');
      return;
    }
    onMusicChange({ ...musicState, youtubeUrl: val });
    addMusicRecent({ url: val.trim(), source: 'youtube', youtubePresentation });
    setRecents(getMusicRecents());
    refreshYouTubeTitleForUrl(val.trim());
  };

  const spotifyEmbed = spotifyUrl ? parseSpotifyEmbed(spotifyUrl) : null;
  const youtubeEmbed = youtubeUrl ? parseYouTubeEmbed(youtubeUrl) : null;
  const youtubePlayerParams = youtubeUrl ? parseYouTubePlayerParams(youtubeUrl) : null;

  const hasActiveEmbed =
    (source === 'spotify' && spotifyEmbed) || (source === 'youtube' && youtubeEmbed);

  const nowPlayingLabel =
    source === 'spotify' ? 'Spotify'
    : source === 'youtube' ? 'YouTube'
    : null;

  const ytLabel = youtubePresentation === 'audio_first' ? 'Audio-first' : 'Standard';

  if (variant === 'dock') {
    return (
      <MotionBox
        layout
        borderRadius="2xl"
        maxW="min(92vw, 440px)"
        mx="auto"
        style={{
          background: 'rgba(8,6,18,0.94)',
          border: `1px solid ${theme.cardBorder}`,
          backdropFilter: 'blur(20px)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
        }}
      >
        <Flex
          align="center"
          justify="center"
          direction="column"
          textAlign="center"
          position="relative"
          px={4}
          py={3}
          cursor={source !== 'none' ? 'pointer' : 'default'}
          onClick={() => source !== 'none' && setDockOpen((o) => !o)}
        >
          {source !== 'none' && (
            <Box as="span" position="absolute" right={3} top="50%" transform="translateY(-50%)" color={theme.textMuted}>
              {dockOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
            </Box>
          )}
          <Box
            p={1.5}
            borderRadius="lg"
            mb={1.5}
            style={{ background: `${accent}18`, border: `1px solid ${accent}28` }}
          >
            <FiMusic size={14} style={{ color: accent }} />
          </Box>
          <Text fontSize="11px" fontWeight="600" letterSpacing="0.1em" textTransform="uppercase" style={{ color: theme.textMuted }}>
            Lounge audio
          </Text>
          <Text fontSize="13px" fontWeight="500" mt={0.5} px={6} style={{ color: theme.textPrimary }}>
            {source === 'none' && 'Off'}
            {source === 'spotify' && (spotifyEmbed ? 'Spotify · music active' : 'Spotify · add link')}
            {source === 'youtube' && (youtubeEmbed ? `YouTube · ${ytLabel}` : 'YouTube · add link')}
          </Text>
        </Flex>
        <AnimatePresence>
          {dockOpen && source !== 'none' && (
            <MotionBox
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28 }}
              overflow="hidden"
              px={4}
              pb={4}
            >
              {source === 'spotify' && (
                <>
                  <MusicUrlRow
                    value={spotifyInput}
                    onChange={handleSpotifyChange}
                    placeholder="Spotify URL…"
                    theme={theme}
                    error={spotifyError}
                    recents={spotifyRecents}
                    menuOpen={recentMenuOpen}
                    onMenuOpenChange={setRecentMenuOpen}
                    onPickRecent={applyRecent}
                    onRemoveRecent={handleRemoveRecent}
                    constrainWidth={false}
                  />
                  {spotifyEmbed && (
                    <Box mt={2} borderRadius="lg" overflow="hidden">
                      <iframe title="Spotify" src={spotifyEmbed} width="100%" height="120" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media" loading="lazy" style={{ display: 'block' }} />
                    </Box>
                  )}
                </>
              )}
              {source === 'youtube' && (
                <>
                  <Flex gap="3px" p="3px" borderRadius="lg" mb={2} style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${theme.cardBorder}` }}>
                    <PresTab label="Audio-first" active={youtubePresentation === 'audio_first'} onClick={() => setPresentation('audio_first')} theme={theme} />
                    <PresTab label="Standard" active={youtubePresentation === 'standard'} onClick={() => setPresentation('standard')} theme={theme} />
                  </Flex>
                  <MusicUrlRow
                    value={youtubeInput}
                    onChange={handleYoutubeChange}
                    placeholder="YouTube URL…"
                    theme={theme}
                    error={youtubeError}
                    recents={youtubeRecents}
                    menuOpen={recentMenuOpen}
                    onMenuOpenChange={setRecentMenuOpen}
                    onPickRecent={applyRecent}
                    onRemoveRecent={handleRemoveRecent}
                    constrainWidth={false}
                  />
                  {youtubeEmbed && youtubePresentation === 'audio_first' && youtubePlayerParams && (
                    <Box mt={2}>
                      <YouTubeAudioDock
                        videoId={youtubePlayerParams.videoId}
                        listId={youtubePlayerParams.listId}
                        theme={theme}
                        accent="#e53935"
                        volume={youtubeVolume}
                        muted={youtubeMuted}
                        onVolumeChange={(v) =>
                          onMusicChange({ ...musicState, youtubeVolume: Math.round(Math.max(0, Math.min(100, v))) })}
                        onMutedChange={(m) => onMusicChange({ ...musicState, youtubeMuted: m })}
                        onSwitchToStandard={() => setPresentation('standard')}
                      />
                    </Box>
                  )}
                  {youtubeEmbed && youtubePresentation === 'standard' && (
                    <Box mt={2}>
                      <YouTubeStandardPlayer src={youtubeEmbed} />
                    </Box>
                  )}
                </>
              )}
            </MotionBox>
          )}
        </AnimatePresence>
      </MotionBox>
    );
  }

  return (
    <Box
      borderRadius="2xl"
      overflow="hidden"
      style={{
        background: `linear-gradient(165deg, ${theme.cardBg} 0%, rgba(255,255,255,0.02) 100%)`,
        border: `1px solid ${theme.cardBorder}`,
        backdropFilter: 'blur(16px)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
      }}
    >
      <Flex direction="column" align="center" textAlign="center" px={4} pt={4} pb={2} gap={2}>
        <Box
          p={1.5}
          borderRadius="lg"
          style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}
        >
          <FiMusic size={14} style={{ color: accent }} />
        </Box>
        <Box>
          <Text fontSize="11px" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" style={{ color: theme.textMuted }}>
            Music lounge
          </Text>
          {hasActiveEmbed && nowPlayingLabel && (
            <Text fontSize="12px" fontWeight="500" mt={0.5} style={{ color: theme.textPrimary }}>
              Music active · {nowPlayingLabel}
              {source === 'youtube' && ` · ${ytLabel}`}
            </Text>
          )}
        </Box>
      </Flex>

      <Box px={3} pb={3} textAlign="center">
        <Flex gap="3px" p="3px" borderRadius="xl" justify="center" style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${theme.cardBorder}` }}>
          <SourceTab label="Off" active={source === 'none'} accent={theme.focusAccent} theme={theme} onClick={() => setSource('none')} />
          <SourceTab label="Spotify" active={source === 'spotify'} accent="#1DB954" theme={theme} onClick={() => setSource('spotify')} />
          <SourceTab label="YouTube" active={source === 'youtube'} accent="#e53935" theme={theme} onClick={() => setSource('youtube')} />
        </Flex>
      </Box>

      {source === 'spotify' && (
        <Box px={4} pb={4} textAlign="center">
          <MusicUrlRow
            value={spotifyInput}
            onChange={handleSpotifyChange}
            placeholder="Paste a Spotify link…"
            theme={theme}
            error={spotifyError}
            recents={spotifyRecents}
            menuOpen={recentMenuOpen}
            onMenuOpenChange={setRecentMenuOpen}
            onPickRecent={applyRecent}
            onRemoveRecent={handleRemoveRecent}
          />
          {spotifyEmbed ? (
            <EmbedChrome accent="#1DB954" theme={theme}>
              <iframe title="Spotify" src={spotifyEmbed} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" style={{ display: 'block' }} />
            </EmbedChrome>
          ) : (
            <Box mt={3} px={3} py={5} borderRadius="xl" textAlign="center" style={{ background: 'rgba(29,185,84,0.06)', border: `1px dashed rgba(29,185,84,0.22)` }}>
              <Text fontSize="12px" lineHeight="1.6" style={{ color: theme.textDim }}>
                Add a playlist or album — playback never leaves this tab
              </Text>
            </Box>
          )}
        </Box>
      )}

      {source === 'youtube' && (
        <Box px={4} pb={4} textAlign="center">
          <Flex mb={3} justify="center" w="100%">
            <Flex gap="3px" p="3px" borderRadius="xl" w="100%" maxW="320px" style={{ background: 'rgba(0,0,0,0.22)', border: `1px solid ${theme.cardBorder}` }}>
              <PresTab label="Audio-first" active={youtubePresentation === 'audio_first'} onClick={() => setPresentation('audio_first')} theme={theme} />
              <PresTab label="Standard" active={youtubePresentation === 'standard'} onClick={() => setPresentation('standard')} theme={theme} />
            </Flex>
          </Flex>
          <MusicUrlRow
            value={youtubeInput}
            onChange={handleYoutubeChange}
            placeholder="Paste a YouTube link…"
            theme={theme}
            error={youtubeError}
            recents={youtubeRecents}
            menuOpen={recentMenuOpen}
            onMenuOpenChange={setRecentMenuOpen}
            onPickRecent={applyRecent}
            onRemoveRecent={handleRemoveRecent}
          />
          {youtubeEmbed && youtubePresentation === 'audio_first' && youtubePlayerParams ? (
            <Box mt={3}>
              <Box
                borderRadius="2xl"
                p="3px"
                style={{
                  background: 'linear-gradient(145deg, rgba(229,57,53,0.2), rgba(255,255,255,0.04))',
                  boxShadow: '0 0 0 1px rgba(229,57,53,0.22), 0 16px 44px rgba(0,0,0,0.35)',
                }}
              >
                <YouTubeAudioDock
                  videoId={youtubePlayerParams.videoId}
                  listId={youtubePlayerParams.listId}
                  theme={theme}
                  accent="#e53935"
                  volume={youtubeVolume}
                  muted={youtubeMuted}
                  onVolumeChange={(v) =>
                    onMusicChange({ ...musicState, youtubeVolume: Math.round(Math.max(0, Math.min(100, v))) })}
                  onMutedChange={(m) => onMusicChange({ ...musicState, youtubeMuted: m })}
                  onSwitchToStandard={() => setPresentation('standard')}
                />
              </Box>
            </Box>
          ) : youtubeEmbed && youtubePresentation === 'standard' ? (
            <EmbedChrome accent="#e53935" theme={theme}>
              <YouTubeStandardPlayer src={youtubeEmbed} />
            </EmbedChrome>
          ) : youtubeEmbed && youtubePresentation === 'audio_first' && !youtubePlayerParams ? (
            <Flex align="center" justify="center" gap={2} mt={3} px={3} py={3} borderRadius="xl" flexWrap="wrap" textAlign="center" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
              <FiAlertCircle size={14} color="#f87171" style={{ flexShrink: 0 }} />
              <Text fontSize="11px" lineHeight="1.5" style={{ color: theme.textMuted }}>
                Audio-first couldn&apos;t read this link. Try Standard mode or another URL.
              </Text>
            </Flex>
          ) : !youtubeEmbed ? (
            <Box mt={3} px={3} py={5} borderRadius="xl" textAlign="center" style={{ background: 'rgba(229,57,53,0.05)', border: '1px dashed rgba(229,57,53,0.18)' }}>
              <Text fontSize="12px" lineHeight="1.6" style={{ color: theme.textDim }}>
                Video, playlist, or live stream — if YouTube allows embedding
              </Text>
            </Box>
          ) : null}
        </Box>
      )}

      {source === 'none' && (
        <Box px={4} pb={5} textAlign="center">
          <Box py={6} borderRadius="xl" style={{ background: theme.inputBg, border: `1px dashed ${theme.cardBorder}` }}>
            <FiMusic size={24} style={{ color: theme.textDim, margin: '0 auto 10px', opacity: 0.7 }} />
            <Text fontSize="13px" fontWeight="500" mb={1} style={{ color: theme.textPrimary }}>Sound is optional</Text>
            <Text fontSize="11px" lineHeight="1.65" px={4} style={{ color: theme.textDim }}>
              Spotify or YouTube — all client-side, nothing uploaded
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MusicPanel;
